/* TypeTopia — face find (v1)
   assets/tt-face-find.js

   v1 (2026-10-07, Ally: "the face thing should only happen if the cutout
   result gives 2 faces, i want a detection of multiple after the cutout & a
   choice to use all or drop"). The cutout worker sometimes keeps several
   people in the one see-through PNG it sends back (a group photo). This
   looks at that cut-out, never at the photo, and only after it's back:

     split(img [, opts])  -> Promise<null | { found, capped, how, pieces }>
       img: the worker's cut-out, loaded (CORS-clean, so it can be read)
       null: one person (or it can't tell): nothing changes at all.
       pieces: one canvas per person, left to right, each cropped to that
       person with everyone else cleared (their own clean cut-out); at most
       opts.max (5). found: how many there were; capped: found > max (the
       biggest ones are kept). how: "blobs" or "faces".

   How:
     1. the cut-out's solid parts (alpha > 127, on a copy at most 480px on
        its long side), as separate regions (8-connected). A region counts
        as a person when it's at least 18% of the biggest one's area and 40%
        of its height (specks, a stray curl, an earring are not: each joins
        the person it's nearest, if it's close, or is left out). Two or more
        people: done, no download.
     2. one region whose top edge has two or more heads (bumps at least 8%
        of its height deep, apart by 18% of its width): people touching.
        Only then is the face detector loaded (MediaPipe Tasks Vision's face
        detector, BlazeFace short range: its script, its wasm and the 230KB
        model, about 3.5MB the first time, from jsdelivr and Google's model
        store), run on the cut-out (on white; and, for a wide one, on square
        windows across it too, merged), and the region shared out between
        the faces it finds (each solid pixel to the nearest face, distance
        across counting three times distance down). Faces under 60% sure or
        under 40% of the biggest's size are ignored. If it fails to load,
        errors or takes over 20s: one person.

   Nothing is stored in the browser and nothing is sent anywhere: the
   callers upload the pieces through the uploads they already use.
   Window global: TTFaceFind. Loaded as a deferred script by
   sections/tt-face-confetti.liquid, tt-wrap-studio.liquid and
   tt-wrap-collection.liquid; split() is also on TTWrapKit's cutoutAll().
*/
(function (G) {
  "use strict";
  if (G.TTFaceFind && G.TTFaceFind.v) return;
  var F = G.TTFaceFind = { v: 1 };

  F.MAX = 5;
  F.MP = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@1.0.1";
  F.MODEL = "https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite";
  var SIDE = 480, ALPHA = 127;
  var LOAD_MS = 20000;

  function canvas(w, h) { var c = document.createElement("canvas"); c.width = w; c.height = h; return c; }

  /* ── 1. the solid regions ── */
  function grid(img) {
    var W = img.naturalWidth || img.width, H = img.naturalHeight || img.height;
    if (!W || !H) return null;
    var s = Math.min(1, SIDE / Math.max(W, H)), w = Math.max(1, Math.round(W * s)), h = Math.max(1, Math.round(H * s));
    var c = canvas(w, h), x = c.getContext("2d", { willReadFrequently: true });
    x.drawImage(img, 0, 0, w, h);
    var d = x.getImageData(0, 0, w, h).data, a = new Uint8Array(w * h);
    for (var i = 0; i < w * h; i++) a[i] = d[i * 4 + 3] > ALPHA ? 1 : 0;
    return { W: W, H: H, w: w, h: h, s: s, a: a };
  }
  function regions(g) {
    var w = g.w, h = g.h, lab = new Int32Array(w * h), out = [], stack = [];
    for (var p = 0; p < w * h; p++) {
      if (!g.a[p] || lab[p]) continue;
      var id = out.length + 1, r = { id: id, n: 0, x0: w, y0: h, x1: -1, y1: -1, sx: 0, sy: 0 };
      lab[p] = id; stack.push(p);
      while (stack.length) {
        var q = stack.pop(), qx = q % w, qy = (q - qx) / w;
        r.n++; r.sx += qx; r.sy += qy;
        if (qx < r.x0) r.x0 = qx; if (qx > r.x1) r.x1 = qx; if (qy < r.y0) r.y0 = qy; if (qy > r.y1) r.y1 = qy;
        for (var dy = -1; dy <= 1; dy++) {
          var ny = qy + dy; if (ny < 0 || ny >= h) continue;
          for (var dx = -1; dx <= 1; dx++) {
            var nx = qx + dx; if (nx < 0 || nx >= w) continue;
            var k = ny * w + nx;
            if (g.a[k] && !lab[k]) { lab[k] = id; stack.push(k); }
          }
        }
      }
      out.push(r);
    }
    g.lab = lab;
    return out;
  }
  function gap(a, b) {
    var dx = Math.max(0, Math.max(a.x0, b.x0) - Math.min(a.x1, b.x1)), dy = Math.max(0, Math.max(a.y0, b.y0) - Math.min(a.y1, b.y1));
    return Math.sqrt(dx * dx + dy * dy);
  }
  /* the people: the big regions; each small one joins the nearest person within 8% of the
     picture's long side (else it's left out). owner[id] = the person's index, or -1 */
  function people(g, rs) {
    if (!rs.length) return null;
    var big = rs.reduce(function (m, r) { return r.n > m.n ? r : m; }, rs[0]), bh = big.y1 - big.y0 + 1;
    var ps = rs.filter(function (r) { return r.n >= 0.18 * big.n && (r.y1 - r.y0 + 1) >= 0.4 * bh; });
    var owner = new Int32Array(rs.length + 1).fill(-1), near = 0.08 * Math.max(g.w, g.h);
    ps.forEach(function (r, i) { owner[r.id] = i; r.parts = [r.id]; });
    rs.forEach(function (r) {
      if (owner[r.id] > -1) return;
      var best = -1, bd = near;
      ps.forEach(function (p, i) { var d = gap(r, p); if (d <= bd) { bd = d; best = i; } });
      if (best > -1) { owner[r.id] = best; ps[best].parts.push(r.id); }
    });
    return { ps: ps, owner: owner };
  }

  /* ── 2. heads on one region's top edge ── */
  function heads(g, r) {
    var w = g.w, bw = r.x1 - r.x0 + 1, bh = r.y1 - r.y0 + 1, top = [];
    for (var x = r.x0; x <= r.x1; x++) {
      var t = r.y1 + 1;
      for (var y = r.y0; y <= r.y1; y++) if (g.lab[y * w + x] === r.id) { t = y; break; }
      top.push(r.y1 + 1 - t);                       /* height of the edge above the region's foot */
    }
    var k = Math.max(1, Math.round(bw * 0.02)), sm = top.map(function (v, i) {
      var s = 0, n = 0; for (var j = i - k; j <= i + k; j++) if (j >= 0 && j < top.length) { s += top[j]; n++; } return s / n;
    });
    /* peaks with prominence: from each local top, how far down the edge goes on both sides
       before it climbs higher than this top again */
    var deep = 0.08 * bh, apart = 0.18 * bw, found = [];
    for (var i = 0; i < sm.length; i++) {
      if ((i > 0 && sm[i - 1] > sm[i]) || (i < sm.length - 1 && sm[i + 1] > sm[i])) continue;
      if (i > 0 && sm[i - 1] === sm[i]) continue;   /* a flat top counts once */
      var lo = sm[i], j, lmin = sm[i], rmin = sm[i];
      for (j = i - 1; j >= 0 && sm[j] <= lo; j--) lmin = Math.min(lmin, sm[j]);
      if (j < 0) lmin = 0;
      for (j = i + 1; j < sm.length && sm[j] <= lo; j++) rmin = Math.min(rmin, sm[j]);
      if (j >= sm.length) rmin = 0;
      if (lo - Math.max(lmin, rmin) >= deep) found.push(i);
    }
    var keep = [];
    found.forEach(function (i) { if (!keep.length || i - keep[keep.length - 1] >= apart) keep.push(i); });
    return keep.length;
  }

  /* ── the face detector, loaded only when it's needed ── */
  var det = null;
  /* MediaPipe is an ES module. Not a dynamic import: Shopify's asset pipeline turns one into require()
     in theme assets, so a tiny module script brings it in and hands it over on a global */
  function mpModule() {
    if (G.__ttMP) return Promise.resolve(G.__ttMP);
    return new Promise(function (res, rej) {
      var s = document.createElement("script"), done = false;
      function fin(ok) { if (done) return; done = true; G.removeEventListener("tt:mp", onOk); ok && G.__ttMP ? res(G.__ttMP) : rej(new Error("mediapipe")); }
      function onOk() { fin(true); }
      G.addEventListener("tt:mp", onOk);
      s.type = "module";
      s.textContent = 'import * as V from ' + JSON.stringify(F.MP + "/vision_bundle.mjs") + '; window.__ttMP = V; window.dispatchEvent(new Event("tt:mp"));';
      s.onerror = function () { fin(false); };
      setTimeout(function () { fin(false); }, LOAD_MS);
      document.head.appendChild(s);
    });
  }
  F.detector = function () {
    if (det) return det;
    det = new Promise(function (res, rej) {
      var t = setTimeout(function () { rej(new Error("detector timeout")); }, LOAD_MS);
      mpModule().then(function (V) {
        return V.FilesetResolver.forVisionTasks(F.MP + "/wasm").then(function (fs) {
          return V.FaceDetector.createFromOptions(fs, {
            baseOptions: { modelAssetPath: F.MODEL, delegate: "CPU" },
            runningMode: "IMAGE", minDetectionConfidence: 0.5
          });
        });
      }).then(function (d) { clearTimeout(t); res(d); }, function (e) { clearTimeout(t); rej(e); });
    });
    det.catch(function () { det = null; });       /* the next group photo tries again */
    return det;
  };
  function iou(a, b) {
    var x0 = Math.max(a.x, b.x), y0 = Math.max(a.y, b.y), x1 = Math.min(a.x + a.w, b.x + b.w), y1 = Math.min(a.y + a.h, b.y + b.h);
    var i = Math.max(0, x1 - x0) * Math.max(0, y1 - y0);
    return i / (a.w * a.h + b.w * b.h - i || 1);
  }
  /* faces in the cut-out (on white), in its pixels; a wide one is also looked at in square
     windows across it, so each face is big enough for the short-range model */
  F.faces = function (img) {
    return F.detector().then(function (d) {
      var W = img.naturalWidth || img.width, H = img.naturalHeight || img.height;
      var s = Math.min(1, 1280 / Math.max(W, H)), w = Math.round(W * s), h = Math.round(H * s);
      var base = canvas(w, h), bx = base.getContext("2d");
      bx.fillStyle = "#fff"; bx.fillRect(0, 0, w, h); bx.drawImage(img, 0, 0, w, h);
      var wins = [{ x: 0, y: 0, w: w, h: h }];
      if (w > h * 1.2) for (var x = 0; x < w; x += h / 2) { wins.push({ x: Math.min(x, w - h), y: 0, w: h, h: h }); if (x + h >= w) break; }
      if (h > w * 1.2) for (var y = 0; y < h; y += w / 2) { wins.push({ x: 0, y: Math.min(y, h - w), w: w, h: w }); if (y + w >= h) break; }
      var all = [];
      wins.forEach(function (r) {
        var c = canvas(Math.round(r.w), Math.round(r.h));
        c.getContext("2d").drawImage(base, r.x, r.y, r.w, r.h, 0, 0, c.width, c.height);
        (d.detect(c).detections || []).forEach(function (dt) {
          var b = dt.boundingBox, sc = dt.categories && dt.categories[0] ? dt.categories[0].score : 0;
          if (!b || sc < 0.6) return;
          all.push({ x: (r.x + b.originX) / s, y: (r.y + b.originY) / s, w: b.width / s, h: b.height / s, score: sc });
        });
      });
      all.sort(function (a, b) { return b.score - a.score; });
      var keep = [];
      all.forEach(function (f) { if (!keep.some(function (k) { return iou(k, f) > 0.3; })) keep.push(f); });
      var big = keep.reduce(function (m, f) { return Math.max(m, f.w); }, 0);
      return keep.filter(function (f) { return f.w >= 0.4 * big; }).sort(function (a, b) { return a.x - b.x; });
    });
  };

  /* ── the pieces: each person on their own, everyone else cleared ── */
  function cut(img, g, mine, box) {
    var W = g.W, H = g.H, pad = Math.round(0.02 * Math.max(W, H));
    var x0 = Math.max(0, Math.floor(box.x0 / g.s) - pad), y0 = Math.max(0, Math.floor(box.y0 / g.s) - pad);
    var x1 = Math.min(W, Math.ceil((box.x1 + 1) / g.s) + pad), y1 = Math.min(H, Math.ceil((box.y1 + 1) / g.s) + pad);
    var c = canvas(x1 - x0, y1 - y0), x = c.getContext("2d", { willReadFrequently: true });
    x.drawImage(img, x0, y0, c.width, c.height, 0, 0, c.width, c.height);
    var im = x.getImageData(0, 0, c.width, c.height), d = im.data, gw = g.w, gh = g.h;
    for (var py = 0; py < c.height; py++) {
      var gy = Math.min(gh - 1, Math.floor((py + y0) * g.s));
      for (var px = 0; px < c.width; px++) {
        var o = (py * c.width + px) * 4;
        if (!d[o + 3]) continue;
        var gx = Math.min(gw - 1, Math.floor((px + x0) * g.s)), v = mine(gy * gw + gx);
        if (v === 0) {                               /* a soft edge: theirs if a neighbour is theirs */
          v = -1;
          for (var dy = -1; dy <= 1 && v < 0; dy++) for (var dx = -1; dx <= 1; dx++) {
            var ny = gy + dy, nx = gx + dx;
            if (ny < 0 || nx < 0 || ny >= gh || nx >= gw) continue;
            var m = mine(ny * gw + nx);
            if (m === 1) { v = 1; break; }
          }
        }
        if (v !== 1) d[o + 3] = 0;
      }
    }
    x.putImageData(im, 0, 0);
    return c;
  }
  function finish(list, how, max) {
    max = max || F.MAX;
    var found = list.length, keep = list.slice().sort(function (a, b) { return b.n - a.n; }).slice(0, max);
    keep.sort(function (a, b) { return a.cx - b.cx; });
    return { found: found, capped: found > max, how: how, pieces: keep.map(function (p) { return p.canvas; }) };
  }

  F.split = function (img, opts) {
    opts = opts || {};
    var g, rs, pp;
    try {
      g = grid(img); if (!g) return Promise.resolve(null);
      rs = regions(g); pp = people(g, rs);
    } catch (e) { return Promise.resolve(null); }    /* a picture the canvas can't read: one person */
    if (!pp || !pp.ps.length) return Promise.resolve(null);
    if (pp.ps.length >= 2) {
      try {
        var list = pp.ps.map(function (p, i) {
          var box = { x0: p.x0, y0: p.y0, x1: p.x1, y1: p.y1 };
          p.parts.forEach(function (id) { var r = rs[id - 1]; box.x0 = Math.min(box.x0, r.x0); box.y0 = Math.min(box.y0, r.y0); box.x1 = Math.max(box.x1, r.x1); box.y1 = Math.max(box.y1, r.y1); });
          var mine = function (k) { var l = g.lab[k]; return !l ? 0 : pp.owner[l] === i ? 1 : -1; };
          return { n: p.n, cx: p.sx / p.n, canvas: cut(img, g, mine, box) };
        });
        return Promise.resolve(finish(list, "blobs", opts.max));
      } catch (e) { return Promise.resolve(null); }
    }
    /* one region: only a top edge with two or more heads is worth the detector */
    var one = pp.ps[0];
    if (heads(g, one) < 2) return Promise.resolve(null);
    return F.faces(img).then(function (fs) {
      if (fs.length < 2) return null;
      var cs = fs.map(function (f) { return { x: (f.x + f.w / 2) * g.s, y: (f.y + f.h / 2) * g.s, n: 0, sx: 0, x0: g.w, y0: g.h, x1: -1, y1: -1 }; });
      var who = new Int8Array(g.w * g.h).fill(-1);
      for (var k = 0; k < g.w * g.h; k++) {
        var l = g.lab[k]; if (!l || pp.owner[l] !== 0) continue;
        var px = k % g.w, py = (k - px) / g.w, best = 0, bd = Infinity;
        for (var i = 0; i < cs.length; i++) {
          var dx = (px - cs[i].x) * 3, dy = py - cs[i].y, dd = dx * dx + dy * dy;
          if (dd < bd) { bd = dd; best = i; }
        }
        who[k] = best; var c = cs[best]; c.n++; c.sx += px;
        if (px < c.x0) c.x0 = px; if (px > c.x1) c.x1 = px; if (py < c.y0) c.y0 = py; if (py > c.y1) c.y1 = py;
      }
      var list = cs.map(function (c, i) {
        if (!c.n) return null;
        var mine = function (k) { var l = g.lab[k]; return !l ? 0 : who[k] === i ? 1 : -1; };
        return { n: c.n, cx: c.sx / c.n, canvas: cut(img, g, mine, c) };
      }).filter(Boolean);
      return list.length >= 2 ? finish(list, "faces", opts.max) : null;
    }).catch(function () { return null; });         /* no detector: one person */
  };

  /* for the tests */
  F._t = { grid: grid, regions: regions, people: people, heads: heads };

  /* a piece as a PNG, for the callers' uploads */
  F.toBlob = function (c) {
    return new Promise(function (res, rej) { c.toBlob(function (b) { b ? res(b) : rej(new Error("export")); }, "image/png"); });
  };
  /* run fn over items, at most n at a time; resolves the results in order (a failure is null) */
  F.pool = function (items, n, fn) {
    var out = new Array(items.length), next = 0;
    function lane() {
      if (next >= items.length) return Promise.resolve();
      var i = next++;
      return Promise.resolve().then(function () { return fn(items[i], i); })
        .then(function (v) { out[i] = v; }, function () { out[i] = null; }).then(lane);
    }
    var lanes = []; for (var k = 0; k < Math.min(n, items.length); k++) lanes.push(lane());
    return Promise.all(lanes).then(function () { return out; });
  };
})(typeof self !== "undefined" ? self : this);
